import type { MagicCommentController, MagicCommentOptions } from "../types";
import { mountMagicComment } from "./controller";
import { readScriptConfig } from "./global-config";

type GuardedWindow = Window & {
  __MAGIC_COMMENT_MOUNTED__?: MagicCommentController;
};

/**
 * Mount the widget imperatively and start it. Returned by `MagicComment.mount`.
 *
 * Idempotent: if a widget is already mounted on this window (script tag
 * included twice, tag-manager re-injection, second manual call), a warning is
 * logged and the existing instance is returned instead of stacking a second
 * UI with duplicate listeners.
 */
export function mount(options: MagicCommentOptions): MagicCommentController {
  if (typeof window === "undefined") {
    const controller = mountMagicComment(options);
    controller.mount();
    return controller;
  }

  const guarded = window as GuardedWindow;
  const existing = guarded.__MAGIC_COMMENT_MOUNTED__;
  if (existing) {
    console.warn(
      "[magic-comment] mount() called while already mounted — returning the existing instance.",
    );
    return existing;
  }

  const controller = mountMagicComment(options);
  const wrapped: MagicCommentController = {
    ...controller,
    unmount() {
      controller.unmount();
      if (guarded.__MAGIC_COMMENT_MOUNTED__ === wrapped) {
        guarded.__MAGIC_COMMENT_MOUNTED__ = undefined;
      }
    },
  };

  guarded.__MAGIC_COMMENT_MOUNTED__ = wrapped;
  wrapped.mount();
  return wrapped;
}

// Auto-mount from the current <script> tag's data-* attributes, unless the tag
// opts out with data-auto-mount="false" or disables the widget entirely with
// data-enabled="false" (the "never on production" kill switch).
if (typeof document !== "undefined") {
  const script = document.currentScript as HTMLScriptElement | null;
  if (!script) {
    console.warn(
      '[magic-comment] Auto-mount skipped: document.currentScript is unavailable (e.g. the script was loaded with type="module" or injected asynchronously). Load the embed as a classic <script> tag, or call MagicComment.mount() manually.',
    );
  } else if (script.dataset.autoMount !== "false") {
    const config = readScriptConfig(script.dataset);
    if (config) {
      if (config.ui?.enabled === false) {
        console.debug(
          '[magic-comment] data-enabled="false" — auto-mount skipped (kill switch).',
        );
      } else {
        mount(config);
      }
    }
  }
}

export { mountMagicComment, readScriptConfig };
